import { useEffect, useMemo, useState, type ReactNode } from "react";
import { z } from "zod";
import {
  ChevronDownIcon,
  Gift,
  InfoIcon,
  Package,
  Palette,
  type Icon,
} from "@/components/icons";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldContent,
  FieldError,
  FieldLabel,
  FieldLayout,
  FormInput,
  FormRoot,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  RHF,
  toast,
  zodResolver,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useProductPickerQuery } from "@/modules/orders/presentation/api/queries";
import type { ProductVariantDTO } from "@/modules/products/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useCreateVariant, useUpdateVariant } from "../api/queries";
import { BundleLinesEditor } from "./BundleLinesEditor";
import { ConversionFactorField } from "./ConversionFactorField";

function emptyToUndefined(value: unknown) {
  if (value === "" || value === null || value === undefined) return undefined;
  return value;
}

const optionalPositiveNumber = z.preprocess(
  emptyToUndefined,
  z.coerce.number().positive().optional(),
);

const VariantFieldsSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "ຕ້ອງມີຊື່"),
  sku: z.string().optional(),
  price: z.coerce.number().int().min(0),
  stockQuantity: z.coerce.number().int().min(0),
  stockUnitLabel: z.string().max(32).optional(),
  receiveUnitLabel: z.string().max(32).optional().nullable(),
  receiveConversionFactor: optionalPositiveNumber,
  imageKey: z.string().optional().nullable(),
  sortOrder: z.coerce.number().int().optional(),
  isActive: z.boolean().optional(),
  isInventoryOnly: z.boolean().optional(),
  inventoryVariantId: z.string().optional().nullable(),
  unitsPerSale: optionalPositiveNumber,
  lowStockThreshold: z.coerce.number().int().min(0).optional(),
  variantType: z.enum(["simple", "bundle"]).optional(),
  bundleLines: z
    .array(
      z.object({
        componentVariantId: z.string().optional().default(""),
        quantity: z.coerce.number().int().min(1).optional().default(1),
        sortOrder: z.coerce.number().int().optional(),
        componentProductId: z.string().optional(),
        componentProductName: z.string().optional(),
        componentVariantName: z.string().optional(),
      }),
    )
    .optional(),
});

const FormSchema = z
  .object({
    variants: z.array(VariantFieldsSchema).length(1),
  })
  .superRefine((data, ctx) => {
    const variant = data.variants[0];
    if (!variant || variant.variantType !== "bundle") return;

    const lines = variant.bundleLines ?? [];
    const validLines = lines.filter(
      (line) => line.componentVariantId?.trim() && line.quantity >= 1,
    );

    if (validLines.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເຊັດຕ້ອງມີສ່ວນປະກອບຢ່າງນ້ອຍ 1 ລາຍການ",
        path: ["variants", 0, "bundleLines"],
      });
      for (let j = 0; j < lines.length; j++) {
        if (!lines[j]?.componentVariantId?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "ເລືອກສິນຄ້າ ແລະ ຕົວເລືອກສ່ວນປະກອບ",
            path: ["variants", 0, "bundleLines", j, "componentVariantId"],
          });
        }
      }
    }
  })
  .transform((data) => {
    const variant = data.variants[0]!;
    if (variant.variantType !== "bundle") {
      const { bundleLines: _bundleLines, ...rest } = variant;
      return { variants: [rest] };
    }
    return {
      variants: [
        {
          ...variant,
          variantType: "bundle" as const,
          bundleLines: (variant.bundleLines ?? [])
            .filter(
              (line) => line.componentVariantId?.trim() && line.quantity >= 1,
            )
            .map(({ componentVariantId, quantity, sortOrder }) => ({
              componentVariantId,
              quantity,
              sortOrder,
            })),
        },
      ],
    };
  });

type FormValues = z.input<typeof FormSchema>;
type ParsedFormValues = z.output<typeof FormSchema>;
type VariantValues = FormValues["variants"][number];
type StockRole = "standalone" | "pool" | "linked";

type InitialVariant =
  | ProductVariantDTO
  | Partial<VariantValues>
  | null
  | undefined;

export type VariantFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  defaultLowStockThreshold: number;
  siblingVariants: ProductVariantDTO[];
  mode: "create" | "edit" | "copy";
  initialVariant?: InitialVariant;
  onSuccess?: (result?: {
    mode: "create" | "edit" | "copy";
    variant?: ProductVariantDTO;
  }) => void;
};

function getVariantRole(variant: VariantValues | undefined): StockRole {
  if (variant?.variantType === "bundle") return "standalone";
  if (variant?.isInventoryOnly) return "pool";
  if (variant?.inventoryVariantId) return "linked";
  return "standalone";
}

function dtoToFormValues(
  source: InitialVariant,
  opts: {
    mode: "create" | "edit" | "copy";
    defaultLowStockThreshold: number;
  },
): VariantValues {
  if (!source) {
    return {
      name: "",
      price: 0,
      stockQuantity: 0,
      stockUnitLabel: "ຊິ້ນ",
      isInventoryOnly: false,
      inventoryVariantId: null,
      unitsPerSale: 1,
      lowStockThreshold: opts.defaultLowStockThreshold,
      variantType: "simple",
      bundleLines: [],
    };
  }

  const isDto = "productId" in source && typeof source.id === "string";
  const name = source.name?.trim() ?? "";
  const baseName = opts.mode === "copy" && name ? `${name} (ສຳເນົາ)` : name;

  return {
    id: opts.mode === "edit" && isDto ? source.id : undefined,
    name: baseName,
    sku: (source.sku as string | null | undefined) ?? "",
    price: Number(source.price ?? 0),
    stockQuantity:
      opts.mode === "copy"
        ? 0
        : source.inventoryVariantId
          ? 0
          : Number(source.stockQuantity ?? 0),
    stockUnitLabel: source.stockUnitLabel ?? "ຊິ້ນ",
    receiveUnitLabel: source.receiveUnitLabel ?? null,
    receiveConversionFactor: Number(
      (source as { receiveConversionFactor?: string | number })
        .receiveConversionFactor ?? 1,
    ),
    imageKey: source.imageKey ?? null,
    sortOrder: source.sortOrder,
    isActive: source.isActive ?? true,
    isInventoryOnly: source.isInventoryOnly ?? false,
    inventoryVariantId: source.inventoryVariantId ?? null,
    unitsPerSale: Number(source.unitsPerSale ?? 1) || 1,
    lowStockThreshold:
      source.lowStockThreshold ?? opts.defaultLowStockThreshold,
    variantType: source.variantType ?? "simple",
    bundleLines:
      (
        source as {
          bundleLines?: Array<{
            componentVariantId: string;
            quantity: number;
            sortOrder?: number;
            componentProductId?: string;
            componentProductName?: string;
            componentVariantName?: string;
          }>;
        }
      ).bundleLines?.map((line) => ({
        componentVariantId: line.componentVariantId,
        quantity: line.quantity,
        sortOrder: line.sortOrder,
        componentProductId: line.componentProductId,
        componentProductName: line.componentProductName,
        componentVariantName: line.componentVariantName,
      })) ?? [],
  };
}

function dialogTitle(mode: VariantFormDialogProps["mode"]) {
  if (mode === "edit") return "ແກ້ໄຂຕົວເລືອກ";
  if (mode === "copy") return "ສຳເນົາຕົວເລືອກ";
  return "ເພີ່ມຕົວເລືອກ";
}

function dialogSubtitle(mode: VariantFormDialogProps["mode"]) {
  if (mode === "edit") return "ປັບຊື່ ລາຄາ ຫຼື ວິທີຈັດສະຕັອກ";
  if (mode === "copy") return "ສ້າງຕົວເລືອກໃໝ່ຈາກຂໍ້ມູນເກົ່າ — ສະຕັອກເລີ່ມທີ່ 0";
  return "ກຳນົດຊື່ ລາຄາ ແລະ ວິທີຈັດສະຕັອກຂອງຕົວເລືອກນີ້";
}

function FormBlock({
  step,
  title,
  description,
  children,
}: {
  step?: number;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-start gap-3">
        {step != null ? (
          <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary/15 font-semibold text-secondary text-xs tabular-nums">
            {step}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-foreground text-sm leading-tight">
            {title}
          </h4>
          {description ? (
            <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function ChoiceCard({
  selected,
  disabled,
  onSelect,
  icon: IconComponent,
  title,
  description,
  accent = "secondary",
}: {
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  icon: Icon;
  title: string;
  description: string;
  accent?: "secondary" | "leaf" | "amber";
}) {
  const accentClass =
    accent === "leaf"
      ? "border-leaf/40 bg-leaf/8 ring-leaf/25"
      : accent === "amber"
        ? "border-amber-600/35 bg-amber-500/8 ring-amber-600/20"
        : "border-secondary/40 bg-secondary/8 ring-secondary/25";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-start gap-3 rounded-2xl border px-3.5 py-3 text-left transition-all",
        "hover:border-secondary/35 hover:bg-secondary/5",
        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
        "disabled:pointer-events-none disabled:opacity-45",
        selected
          ? cn("shadow-sm ring-1", accentClass)
          : "border-secondary/15 bg-background/70",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl",
          selected
            ? accent === "leaf"
              ? "bg-leaf/15 text-leaf"
              : accent === "amber"
                ? "bg-amber-500/15 text-amber-800"
                : "bg-secondary/15 text-secondary"
            : "bg-muted text-muted-foreground",
        )}
      >
        <IconComponent className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-sm leading-snug">{title}</span>
        <span className="mt-0.5 block text-muted-foreground text-xs leading-relaxed">
          {description}
        </span>
      </span>
      <span
        className={cn(
          "mt-1 size-4 shrink-0 rounded-full border-2 transition-colors",
          selected
            ? accent === "leaf"
              ? "border-leaf bg-leaf"
              : accent === "amber"
                ? "border-amber-700 bg-amber-700"
                : "border-secondary bg-secondary"
            : "border-muted-foreground/35",
        )}
        aria-hidden
      />
    </button>
  );
}

export function VariantFormDialog({
  open,
  onOpenChange,
  productId,
  defaultLowStockThreshold,
  siblingVariants,
  mode,
  initialVariant,
  onSuccess,
}: VariantFormDialogProps) {
  const createVariant = useCreateVariant(productId);
  const updateVariant = useUpdateVariant(productId);
  const componentPicker = useProductPickerQuery({ purpose: "order" });
  const [showReceiveUnits, setShowReceiveUnits] = useState(false);

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      variants: [
        dtoToFormValues(null, {
          mode: "create",
          defaultLowStockThreshold,
        }),
      ],
    },
  });

  useEffect(() => {
    if (!open) return;
    const next = dtoToFormValues(initialVariant, {
      mode,
      defaultLowStockThreshold,
    });
    methods.reset({ variants: [next] });
    setShowReceiveUnits(Boolean(next.receiveUnitLabel?.trim()));
  }, [open, mode, initialVariant, defaultLowStockThreshold, methods]);

  const watched = methods.watch("variants.0");
  const role = getVariantRole(watched);
  const isBundle = watched?.variantType === "bundle";
  const isPool = role === "pool";
  const saleUnit = watched?.stockUnitLabel?.trim() || "ຫນ່ວຍຂາຍ";
  const unitsPerSale = Number(watched?.unitsPerSale) || 0;

  const poolOptions = useMemo(() => {
    const excludeId =
      mode === "edit" && initialVariant && "id" in initialVariant
        ? initialVariant.id
        : undefined;
    return siblingVariants
      .filter((v) => v.isInventoryOnly && v.id !== excludeId)
      .map((v) => ({
        value: v.id,
        label: v.name?.trim() || `ຄລັງ ${v.id.slice(0, 6)}`,
      }));
  }, [siblingVariants, mode, initialVariant]);

  const hasPools = poolOptions.length > 0;

  const linkedPool = useMemo(() => {
    if (!watched?.inventoryVariantId) return null;
    return (
      siblingVariants.find((v) => v.id === watched.inventoryVariantId) ?? null
    );
  }, [siblingVariants, watched?.inventoryVariantId]);

  const poolUnit = linkedPool?.stockUnitLabel?.trim() || null;
  const submitting = createVariant.isPending || updateVariant.isPending;

  function applyStockRole(nextRole: StockRole) {
    if (nextRole === "pool") {
      methods.setValue("variants.0.isInventoryOnly", true, {
        shouldDirty: true,
      });
      methods.setValue("variants.0.inventoryVariantId", null);
      methods.setValue("variants.0.unitsPerSale", 1);
    } else if (nextRole === "linked") {
      methods.setValue("variants.0.isInventoryOnly", false, {
        shouldDirty: true,
      });
      methods.setValue("variants.0.stockQuantity", 0);
      methods.setValue("variants.0.receiveUnitLabel", null);
      methods.setValue("variants.0.receiveConversionFactor", 1);
      setShowReceiveUnits(false);
      if (!methods.getValues("variants.0.unitsPerSale")) {
        methods.setValue("variants.0.unitsPerSale", 1);
      }
      if (
        !methods.getValues("variants.0.inventoryVariantId") &&
        poolOptions[0]
      ) {
        methods.setValue("variants.0.inventoryVariantId", poolOptions[0].value);
      }
    } else {
      methods.setValue("variants.0.isInventoryOnly", false, {
        shouldDirty: true,
      });
      methods.setValue("variants.0.inventoryVariantId", null);
      methods.setValue("variants.0.unitsPerSale", 1);
      methods.setValue("variants.0.receiveUnitLabel", null);
      methods.setValue("variants.0.receiveConversionFactor", undefined);
      setShowReceiveUnits(false);
    }
  }

  async function handleSubmit(values: FormValues) {
    const parsed = values as unknown as ParsedFormValues;
    const variant = parsed.variants[0];
    if (!variant) return;

    const payload = {
      name: variant.name,
      sku: variant.sku || null,
      price: variant.price,
      stockQuantity: variant.stockQuantity,
      stockUnitLabel: variant.stockUnitLabel,
      receiveUnitLabel: variant.receiveUnitLabel ?? null,
      receiveConversionFactor: variant.receiveConversionFactor,
      imageKey: variant.imageKey ?? null,
      sortOrder: variant.sortOrder,
      isActive: variant.isActive,
      isInventoryOnly: variant.isInventoryOnly,
      inventoryVariantId: variant.inventoryVariantId ?? null,
      unitsPerSale: variant.unitsPerSale,
      lowStockThreshold: variant.lowStockThreshold,
      variantType: variant.variantType,
      bundleLines: "bundleLines" in variant ? variant.bundleLines : undefined,
    };

    try {
      if (mode === "edit" && variant.id) {
        await updateVariant.mutateAsync({
          variantId: variant.id,
          ...payload,
        });
        toast.success("ອັບເດດຕົວເລືອກສຳເລັດ");
        onSuccess?.({ mode: "edit", variant: initialVariant ?? undefined });
      } else {
        const beforeIds = new Set(siblingVariants.map((v) => v.id));
        const detail = await createVariant.mutateAsync(payload);
        const created =
          detail.variants.find((v) => !beforeIds.has(v.id)) ??
          detail.variants.at(-1);
        toast.success(
          mode === "copy" ? "ສຳເນົາຕົວເລືອກສຳເລັດ" : "ເພີ່ມຕົວເລືອກສຳເລັດ",
        );
        onSuccess?.({
          mode: mode === "copy" ? "copy" : "create",
          variant: created,
        });
      }
      onOpenChange(false);
    } catch (e) {
      toast.error("ບັນທຶກຕົວເລືອກລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92dvh,840px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="shrink-0 space-y-1 border-secondary/15 border-b bg-gradient-to-b from-secondary/8 to-transparent px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">
            {dialogTitle(mode)}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {dialogSubtitle(mode)}
          </DialogDescription>
        </DialogHeader>

        <FormRoot
          methods={methods}
          onSubmit={handleSubmit}
          onInvalid={() => {
            toast.error("ກະລຸນາກວດສອບຂໍ້ມູນໃນຟອມ — ລາຍການທີ່ຂີດແດງຍັງບໍ່ຄົບ");
          }}
          formClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
          className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden"
        >
          <div className="min-h-0 flex-1 space-y-7 overflow-y-auto overscroll-contain px-6 py-5">
            <FormBlock
              step={1}
              title="ຂໍ້ມູນພື້ນຖານ"
              description="ຊື່ທີ່ເຫັນໃນອໍເດີ້ ແລະ ລາຄາຂາຍ"
            >
              <div className="space-y-3">
                <FormInput
                  name="variants.0.name"
                  label="ຊື່ຕົວເລືອກ"
                  placeholder="ຕົວຢ່າງ: ສີຊມພູ 3mm"
                  requiredMark
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <RHF.Controller
                    name="variants.0.price"
                    control={methods.control}
                    render={({ field: priceField, fieldState }) => (
                      <FieldLayout
                        data-invalid={fieldState.invalid || undefined}
                      >
                        <FieldLabel htmlFor="variants.0.price">
                          {isPool ? "ລາຄາ (ບໍ່ໃຊ້ຂາຍ)" : "ລາຄາ (ກີບ)"}
                        </FieldLabel>
                        <FieldContent>
                          <InputGroup>
                            <InputGroupAddon>
                              <InputGroupText>₭</InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                              id="variants.0.price"
                              type="number"
                              min={0}
                              step={1}
                              placeholder="0"
                              disabled={isPool}
                              aria-invalid={fieldState.invalid}
                              {...priceField}
                              onChange={(e) =>
                                priceField.onChange(e.target.value)
                              }
                            />
                          </InputGroup>
                          {fieldState.error ? (
                            <FieldError>{fieldState.error.message}</FieldError>
                          ) : null}
                        </FieldContent>
                      </FieldLayout>
                    )}
                  />
                  <FormInput
                    name="variants.0.sku"
                    label="SKU (ທາງເລືອກ)"
                    placeholder="ລະຫັດພາຍໃນ"
                  />
                </div>
              </div>
            </FormBlock>

            <FormBlock
              step={2}
              title="ປະເພດຕົວເລືອກ"
              description="ເລືອກວ່າເປັນສິນຄ້າຂາຍປົກກະຕິ ຫຼື ເຊັດປະກອບ"
            >
              <div className="grid gap-2 sm:grid-cols-2">
                <ChoiceCard
                  selected={!isBundle}
                  icon={Palette}
                  title="ຂາຍປົກກະຕິ"
                  description="ສີ / ຂະໜາດ / ຫນ່ວຍ — ມີສະຕັອກຂອງຕົນເອງ ຫຼື ດຶງຈາກຄລັງ"
                  onSelect={() => {
                    methods.setValue("variants.0.variantType", "simple", {
                      shouldDirty: true,
                    });
                    methods.setValue("variants.0.bundleLines", [], {
                      shouldDirty: true,
                    });
                  }}
                />
                <ChoiceCard
                  selected={isBundle}
                  icon={Gift}
                  accent="amber"
                  title="ເຊັດ / ຊຸດ"
                  description="ປະກອບຈາກຫຼາຍສິນຄ້າ — ສະຕັອກຫຼຸດເມື່ອປະກອບຊຸດ"
                  onSelect={() => {
                    methods.setValue("variants.0.variantType", "bundle", {
                      shouldDirty: true,
                    });
                    applyStockRole("standalone");
                    methods.setValue("variants.0.stockUnitLabel", "ຊຸດ", {
                      shouldDirty: true,
                    });
                    methods.setValue("variants.0.receiveUnitLabel", null, {
                      shouldDirty: true,
                    });
                    methods.setValue(
                      "variants.0.receiveConversionFactor",
                      undefined,
                      { shouldDirty: true },
                    );
                    if (
                      (methods.getValues("variants.0.bundleLines") ?? [])
                        .length === 0
                    ) {
                      methods.setValue(
                        "variants.0.bundleLines",
                        [
                          {
                            componentVariantId: "",
                            quantity: 1,
                            sortOrder: undefined,
                          },
                        ],
                        { shouldDirty: true },
                      );
                    }
                  }}
                />
              </div>
            </FormBlock>

            {isBundle ? (
              <FormBlock
                step={3}
                title="ສ່ວນປະກອບເຊັດ"
                description="ເລືອກສິນຄ້າທີ່ຢູ່ໃນຊຸດ ແລະ ຈຳນວນຕໍ່ 1 ຊຸດ"
              >
                <div className="space-y-4 rounded-2xl border border-amber-600/20 bg-amber-500/5 p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormInput
                      name="variants.0.stockQuantity"
                      label="ສະຕັອກຊຸດເລີ່ມຕົ້ນ"
                      type="number"
                      min={0}
                    />
                    <FormInput
                      name="variants.0.stockUnitLabel"
                      label="ຫນ່ວຍ"
                      placeholder="ຊຸດ"
                    />
                  </div>
                  <BundleLinesEditor
                    variantIndex={0}
                    pickerProducts={componentPicker.data?.data ?? []}
                    pickerReady={!componentPicker.isLoading}
                    excludeVariantIds={
                      mode === "edit" &&
                      initialVariant &&
                      "id" in initialVariant &&
                      initialVariant.id
                        ? [initialVariant.id]
                        : undefined
                    }
                  />
                  <FormInput
                    name="variants.0.lowStockThreshold"
                    label="ເຕືອນໃກ້ໝົດ"
                    type="number"
                    min={0}
                    hint="ແຈ້ງເຕືອນເມື່ອສະຕັອກຊຸດ ≤ ເລກນີ້"
                  />
                </div>
              </FormBlock>
            ) : (
              <>
                <FormBlock
                  step={3}
                  title="ວິທີຈັດສະຕັອກ"
                  description="ເລືອກຮູບແບບທີ່ເໝາະກັບສິນຄ້າ — ສ່ວນໃຫຍ່ໃຊ້ «ສະຕັອກແຍກ»"
                >
                  <div className="grid gap-2">
                    <ChoiceCard
                      selected={role === "standalone"}
                      icon={Package}
                      title="ສະຕັອກແຍກ"
                      description="ຮັບເຂົ້າ ແລະ ຂາຍດ້ວຍຕົວເລືອກນີ້ໂດຍຕົງ — ງ່າຍທີ່ສຸດ"
                      onSelect={() => applyStockRole("standalone")}
                    />
                    <ChoiceCard
                      selected={role === "pool"}
                      icon={Package}
                      title="ຄລັງຮ່ວມ"
                      description="ຮັບເຂົ້າເທົ່ານັ້ນ ແລ້ວໃຫ້ຂະໜາດອື່ນດຶງໄປຂາຍ — ບໍ່ສະແດງໃນອໍເດີ້"
                      onSelect={() => applyStockRole("pool")}
                    />
                    <ChoiceCard
                      selected={role === "linked"}
                      disabled={!hasPools && role !== "linked"}
                      icon={Package}
                      accent="leaf"
                      title="ໃຊ້ຄລັງຮ່ວມ"
                      description={
                        hasPools || role === "linked"
                          ? "ຂາຍໂດຍດຶງຈາກຄລັງຮ່ວມ — ເຊັ່ນ ມັດ 10 / ມັດ 20 ຈາກຄລັງເສັ້ນ"
                          : "ຕ້ອງສ້າງຕົວເລືອກແບບ «ຄລັງຮ່ວມ» ກ່ອນ"
                      }
                      onSelect={() => applyStockRole("linked")}
                    />
                  </div>
                </FormBlock>

                <FormBlock
                  step={4}
                  title={
                    role === "pool"
                      ? "ຫນ່ວຍ ແລະ ສະຕັອກຄລັງ"
                      : role === "linked"
                        ? "ເຊື່ອມຄລັງ ແລະ ອັດຕາແປງ"
                        : "ຫນ່ວຍ ແລະ ສະຕັອກ"
                  }
                >
                  <div
                    className={cn(
                      "space-y-4 rounded-2xl border p-4",
                      role === "linked"
                        ? "border-leaf/25 bg-leaf/5"
                        : role === "pool"
                          ? "border-secondary/25 bg-secondary/5"
                          : "border-secondary/15 bg-muted/20",
                    )}
                  >
                    {role === "standalone" ? (
                      <>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <FormInput
                            name="variants.0.stockQuantity"
                            label="ສະຕັອກເລີ່ມຕົ້ນ"
                            type="number"
                            min={0}
                          />
                          <FormInput
                            name="variants.0.stockUnitLabel"
                            label="ຫນ່ວຍຂາຍ"
                            placeholder="ຊິ້ນ"
                          />
                        </div>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-2 rounded-xl px-1 py-1 text-muted-foreground text-xs transition-colors hover:text-foreground"
                          onClick={() => setShowReceiveUnits((v) => !v)}
                        >
                          <span>ຫນ່ວຍຮັບເຂົ້າຕ່າງຈາກຫນ່ວຍຂາຍ (ທາງເລືອກ)</span>
                          <ChevronDownIcon
                            className={cn(
                              "size-4 transition-transform",
                              showReceiveUnits && "rotate-180",
                            )}
                          />
                        </button>
                        {showReceiveUnits ? (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <FormInput
                              name="variants.0.receiveUnitLabel"
                              label="ຫນ່ວຍຮັບເຂົ້າ"
                              placeholder="ມັດ"
                            />
                            <ConversionFactorField
                              name="variants.0.receiveConversionFactor"
                              label="ອັດຕາແປງ → ຫນ່ວຍຂາຍ"
                              stockUnitField="variants.0.stockUnitLabel"
                              receiveUnitField="variants.0.receiveUnitLabel"
                              targetUnitHint="ຫນ່ວຍຂາຍ"
                              placeholder="100 = 1 ມັດ = 100 ຊິ້ນ"
                            />
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {role === "pool" ? (
                      <>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <FormInput
                            name="variants.0.stockUnitLabel"
                            label="ຫນ່ວຍຄລັງ (ຍ່ອຍສຸດ)"
                            placeholder="ເສັ້ນ"
                          />
                          <FormInput
                            name="variants.0.stockQuantity"
                            label="ສະຕັອກເລີ່ມຕົ້ນ"
                            type="number"
                            min={0}
                          />
                        </div>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-2 rounded-xl px-1 py-1 text-muted-foreground text-xs transition-colors hover:text-foreground"
                          onClick={() => setShowReceiveUnits((v) => !v)}
                        >
                          <span>ຫນ່ວຍຮັບເຂົ້າຕ່າງຈາກຫນ່ວຍຄລັງ (ທາງເລືອກ)</span>
                          <ChevronDownIcon
                            className={cn(
                              "size-4 transition-transform",
                              showReceiveUnits && "rotate-180",
                            )}
                          />
                        </button>
                        {showReceiveUnits ? (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <FormInput
                              name="variants.0.receiveUnitLabel"
                              label="ຫນ່ວຍຮັບເຂົ້າ"
                              placeholder="ມັດ"
                            />
                            <ConversionFactorField
                              name="variants.0.receiveConversionFactor"
                              label="ອັດຕາແປງ → ຫນ່ວຍຄລັງ"
                              stockUnitField="variants.0.stockUnitLabel"
                              receiveUnitField="variants.0.receiveUnitLabel"
                              targetUnitHint="ຫນ່ວຍຄລັງ"
                              placeholder="100 = 1 ມັດ = 100 ເສັ້ນ"
                            />
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {role === "linked" ? (
                      <>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <RHF.Controller
                            name="variants.0.inventoryVariantId"
                            control={methods.control}
                            render={({ field: poolField }) => (
                              <FieldLayout>
                                <FieldLabel>ດຶງຈາກຄລັງຮ່ວມ</FieldLabel>
                                <FieldContent>
                                  <SimpleSelect
                                    value={poolField.value ?? ""}
                                    onValueChange={(value) => {
                                      poolField.onChange(value || null);
                                    }}
                                    placeholder="ເລືອກຄລັງຮ່ວມ"
                                    className="w-full"
                                    options={poolOptions}
                                  />
                                </FieldContent>
                              </FieldLayout>
                            )}
                          />
                          <FormInput
                            name="variants.0.stockUnitLabel"
                            label="ຫນ່ວຍຂາຍ"
                            placeholder="ມັດ 20"
                          />
                        </div>
                        <FormInput
                          name="variants.0.unitsPerSale"
                          label={`1 ${saleUnit} = ? ${poolUnit ?? "ຫນ່ວຍຄລັງ"}`}
                          type="number"
                          min={0.0001}
                          step="any"
                          placeholder="2"
                        />
                        <div className="flex items-start gap-2 rounded-xl border border-leaf/25 bg-background/70 px-3 py-2.5 text-xs leading-relaxed">
                          <InfoIcon className="mt-0.5 size-4 shrink-0 text-leaf" />
                          <span>
                            {poolUnit && unitsPerSale > 0 ? (
                              <>
                                ຂາຍ 1{" "}
                                <strong className="text-foreground">
                                  {saleUnit}
                                </strong>{" "}
                                → ຕັດຄລັງ{" "}
                                <strong className="text-foreground">
                                  {unitsPerSale} {poolUnit}
                                </strong>
                                . ຖ້າຄລັງມີ 100 {poolUnit} → ຂາຍໄດ້{" "}
                                <strong className="text-foreground">
                                  {Math.floor(100 / unitsPerSale)} {saleUnit}
                                </strong>
                              </>
                            ) : (
                              "ເລືອກຄລັງຮ່ວມ ແລະ ໃສ່ອັດຕາແປງເພື່ອເຫັນຕົວຢ່າງ"
                            )}
                          </span>
                        </div>
                      </>
                    ) : null}

                    <div className="border-secondary/15 border-t pt-4">
                      <FormInput
                        name="variants.0.lowStockThreshold"
                        label="ເຕືອນໃກ້ໝົດ"
                        type="number"
                        min={0}
                        hint="ແຈ້ງເຕືອນເມື່ອສະຕັອກຂາຍໄດ້ ≤ ເລກນີ້"
                      />
                    </div>
                  </div>
                </FormBlock>
              </>
            )}
          </div>

          <DialogFooter className="shrink-0 gap-2 border-secondary/15 border-t bg-card/80 px-6 py-4 backdrop-blur-sm">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              className="rounded-full px-6"
              isLoading={submitting}
            >
              {mode === "edit"
                ? "ບັນທຶກ"
                : mode === "copy"
                  ? "ສຳເນົາ"
                  : "ເພີ່ມຕົວເລືອກ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
